import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";

import Select from "antd/es/select";
import { useIntl } from "react-intl";

import DealStageType from "@mapmycustomers/shared/enum/DealStageType";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import { Alert, Labeled } from "@mapmycustomers/ui";

import Checkbox from "@app/component/input/Checkbox";
import SelectField from "@app/component/input/SelectField";
import defaultFilterOption from "@app/component/input/utils/defaultFilterOption";
import {
  updateHeatMapFunnels,
  updateHeatMapOpenStages,
  updateHeatMapStageTypes,
} from "@app/scene/map/store/actions";
import HeatMapLayerData from "@app/scene/map/store/layers/heatMap/HeatMapLayerData";
import { getFunnels, getFunnelStages } from "@app/store/deal";
import { ensureStagesFetched } from "@app/store/deal/actions";
import { RootState } from "@app/store/rootReducer";
import { getDealStageTypeDisplayName } from "@app/util/ui";

import styles from "./DealHeatMapSettings.module.scss";

interface Props {
  data: HeatMapLayerData;
  fetchStages: (funnel: Funnel["id"][]) => void;
  funnels: Funnel[];
  onUpdateHeatMapFunnels: (payload: Funnel["id"][]) => void;
  onUpdateHeatMapOpenStages: (payload: Stage["id"][]) => void;
  onUpdateHeatMapStageTypes: (payload: DealStageType[]) => void;
  openStages: Stage[];
}

const DealHeatMapSettings: React.FC<Props> = ({
  data,
  fetchStages,
  funnels,
  onUpdateHeatMapFunnels,
  onUpdateHeatMapOpenStages,
  onUpdateHeatMapStageTypes,
  openStages,
}) => {
  const intl = useIntl();

  useEffect(() => {
    if (data.funnelIds.length === 1) {
      fetchStages(data.funnelIds);
    }
  }, [data.funnelIds, fetchStages]);

  const handleChangeFunnels = useCallback(
    (funnelIds: Funnel["id"][]) =>
      onUpdateHeatMapFunnels?.(funnelIds.length === funnels.length ? [] : funnelIds),
    [funnels, onUpdateHeatMapFunnels]
  );

  // Show Open stages checkbox as indeterminate when...
  const isDefaultStageCheckboxIndeterminate =
    // Open stage type is selected
    data.stageTypes.includes(DealStageType.DEFAULT) &&
    // AND there's only one funnel or only one funnel selected
    (data.funnelIds.length === 1 || (!data.funnelIds.length && funnels.length === 1)) &&
    // AND some open stages are not selected
    data.openStageIds.length > 0 &&
    data.openStageIds.length !== openStages.length;

  const handleToggleType = useCallback(
    (type: DealStageType, checked: boolean) => {
      // auto-select all open stages when clicked on an unchecked or indeterminate Open stages checkbox
      if (isDefaultStageCheckboxIndeterminate || (type === DealStageType.DEFAULT && checked)) {
        onUpdateHeatMapOpenStages?.(openStages.map(({ id }) => id));
        if (isDefaultStageCheckboxIndeterminate) {
          // don't uncheck the Open stages checkbox
          return;
        }
      }

      const stageTypes = checked
        ? [...data.stageTypes, type]
        : data.stageTypes.filter((stageType) => stageType !== type);
      onUpdateHeatMapStageTypes?.(stageTypes);
    },
    [
      isDefaultStageCheckboxIndeterminate,
      data.stageTypes,
      onUpdateHeatMapOpenStages,
      onUpdateHeatMapStageTypes,
      openStages,
    ]
  );

  const handleToggleOpenStage = useCallback(
    (stage: Stage, checked: boolean) => {
      const stageIds = checked
        ? [...data.openStageIds, stage.id]
        : (data.openStageIds.length ? data.openStageIds : openStages.map(({ id }) => id)).filter(
            (id) => id !== stage.id
          );
      onUpdateHeatMapOpenStages?.(stageIds);
      if (!stageIds.length && data.stageTypes.includes(DealStageType.DEFAULT)) {
        // if all stages unchecked, uncheck the Open stages checkbox too
        onUpdateHeatMapStageTypes?.(
          data.stageTypes.filter((stageType) => stageType !== DealStageType.DEFAULT)
        );
      } else if (stageIds.length && !data.stageTypes.includes(DealStageType.DEFAULT)) {
        // if at least one stage selected, but the Open stages checkbox not, then select it too
        onUpdateHeatMapStageTypes?.([...data.stageTypes, DealStageType.DEFAULT]);
      }
    },
    [
      data.openStageIds,
      data.stageTypes,
      onUpdateHeatMapOpenStages,
      onUpdateHeatMapStageTypes,
      openStages,
    ]
  );

  return (
    <div className={styles.container}>
      <Labeled
        label={intl.formatMessage({
          id: "map.layer.heatMap.edit.funnels",
          defaultMessage: "Select Funnels",
          description: "Funnels selection in Edit Heat Map panel on the Map page",
        })}
      >
        {/* TODO: extract into its own component in /src/component/input */}
        <SelectField<Funnel["id"][]>
          className={styles.funnelSelect}
          filterOption={defaultFilterOption}
          maxTagCount="responsive"
          mode="multiple"
          onChange={handleChangeFunnels}
          value={data.funnelIds.length ? data.funnelIds : funnels.map(({ id }) => id)}
        >
          {funnels.map(({ id, name }) => (
            <Select.Option key={id} label={name} value={id}>
              {name}
            </Select.Option>
          ))}
        </SelectField>
      </Labeled>
      <Labeled
        className={styles.stageTypeLabel}
        label={intl.formatMessage({
          id: "map.layer.heatMap.edit.stages",
          defaultMessage: "Select Types of Deal Revenue",
          description: "Stages selection in Edit Heat Map panel on the Map page",
        })}
      >
        {!data.stageTypes.length && (
          <Alert
            className={styles.noStagesAlert}
            message={intl.formatMessage({
              id: "map.layer.heatMap.edit.stages.noneSelected",
              defaultMessage: "Select one to generate heat map data on the map",
              description:
                "A warning to show when no deal stage types are selected in Heat map settings",
            })}
            showIcon
            type="warning"
          />
        )}
        <div className={styles.stageTypeCheckboxes}>
          {[DealStageType.WON, DealStageType.LOST, DealStageType.DEFAULT].map((type) => (
            <div key={type}>
              <Checkbox
                checked={data.stageTypes.includes(type)}
                indeterminate={
                  type === DealStageType.DEFAULT ? isDefaultStageCheckboxIndeterminate : undefined
                }
                onChange={(checked) => handleToggleType(type, checked)}
              >
                {getDealStageTypeDisplayName(intl, type)}
              </Checkbox>
              {type === DealStageType.DEFAULT &&
                data.funnelIds.length === 1 &&
                data.stageTypes.includes(DealStageType.DEFAULT) && (
                  <div className={styles.openStagesCheckboxes}>
                    {openStages.map((stage) => (
                      <div key={stage.id}>
                        <Checkbox
                          checked={
                            data.openStageIds.length === 0 || data.openStageIds.includes(stage.id)
                          }
                          onChange={(checked) => handleToggleOpenStage(stage, checked)}
                        >
                          {stage.name}
                        </Checkbox>
                      </div>
                    ))}
                  </div>
                )}
            </div>
          ))}
        </div>
      </Labeled>
    </div>
  );
};

const mapStateToProps = (state: RootState, ownProps: { data: HeatMapLayerData }) => ({
  funnels: getFunnels(state),
  openStages: (ownProps.data.funnelIds.length === 1
    ? getFunnelStages(state)[ownProps.data.funnelIds[0]] ?? []
    : []
  ).filter(({ type }) => type === DealStageType.DEFAULT),
});

const mapDispatchToProps = {
  fetchStages: ensureStagesFetched.request,
  onUpdateHeatMapFunnels: updateHeatMapFunnels,
  onUpdateHeatMapOpenStages: updateHeatMapOpenStages,
  onUpdateHeatMapStageTypes: updateHeatMapStageTypes,
};

export default connect(mapStateToProps, mapDispatchToProps)(DealHeatMapSettings);
